import React from "react";
import { CartesianGrid, Line, LineChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";
import styled from "styled-components";
import NoDataPlaceholder from "src/components/no-data-placeholder";
import { format, isValid } from "date-fns";
import { Stack } from "@mui/material";

const TooltipContainer = styled.div`
    background-color: ${({ theme }) => theme.palette.background.tooltip};
    padding: 0.5rem;
    min-width: 10rem;
    align-items: center;
    flex-direction: column;
    border-radius: 6px;
`;

const TooltipTitle = styled.p`
    margin-top: 0;
    margin-bottom: 0.5rem;
`;

const TooltipSubtitle = styled.p`
    margin-top: 0;
    margin-bottom: 0.5rem;
    font-size: 11px;
    font-weight: 300;
    opacity: 0.8;
`;

const TooltipContent = styled.p`
    font-size: 12px;
    margin: 0;
    color: ${({ color }) => color};
`;

const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
        return (
            <TooltipContainer role="status" aria-live="assertive">
                <TooltipTitle>
                    {isValid(
                        new Date(payload[0].payload.timestamp * 1000 || payload[0].payload.datetime)
                    ) &&
                        format(
                            new Date(
                                payload[0].payload.timestamp * 1000 || payload[0].payload.datetime
                            ),
                            "EEE d MMM"
                        )}
                </TooltipTitle>
                <TooltipSubtitle>7 days moving average</TooltipSubtitle>
                {payload.map((entry) => (
                    <Stack direction="row" spacing={0.5}>
                        <TooltipContent color={entry.color}>{entry.dataKey} :</TooltipContent>
                        <TooltipContent>
                            {entry.name === "rank" ? entry.value * -1 : entry.value.toFixed(1)}
                        </TooltipContent>
                    </Stack>
                ))}
            </TooltipContainer>
        );
    }
    return null;
};

const OverviewTimeline = ({ data }) => {
    if (!data || !data.length) return <NoDataPlaceholder />;

    return (
        <ResponsiveContainer width="100%" height="100%">
            <LineChart
                accessibilityLayer
                height="100%"
                width="100%"
                style={{ zIndex: 100 }}
                data={data.map(
                    ({ timestamp, datetime, overall, work, collaboration, wellbeing }) => ({
                        overall,
                        work,
                        collaboration,
                        wellbeing,
                        timestamp,
                        datetime,
                        date:
                            isValid(new Date(datetime || timestamp * 1000)) &&
                            format(new Date(timestamp * 1000 || datetime), "d MMM"),
                    })
                )}
                margin={{ top: 25, right: 32, left: 32, bottom: 10 }}
            >
                <CartesianGrid strokeDasharray="3 3" opacity={0.2} horizontal />
                <XAxis
                    dataKey="date"
                    interval="0"
                    tick={{ fill: "#EAF0F4", opacity: "0.3", fontSize: "12px", fontWeight: "300" }}
                />
                <Tooltip
                    cursor={{ opacity: "0.2" }}
                    content={CustomTooltip}
                    position={{ y: 120 }}
                />
                <Line
                    type="basis"
                    yAxisId="left"
                    dataKey="work"
                    stroke="#13a7ad"
                    fill="#13a7ad"
                    activeDot={{ r: 3 }}
                    dot={{ r: 0 }}
                    isAnimationActive={false}
                />
                <Line
                    type="basis"
                    yAxisId="left"
                    dataKey="collaboration"
                    stroke="#F24AE1"
                    fill="#F24AE1"
                    activeDot={{ r: 3 }}
                    dot={{ r: 0 }}
                    isAnimationActive={false}
                />
                <Line
                    type="basis"
                    yAxisId="left"
                    dataKey="wellbeing"
                    stroke="#F2994A"
                    fill="#F2994A"
                    activeDot={{ r: 3 }}
                    dot={{ r: 0 }}
                    isAnimationActive={false}
                />
                <Line
                    type="basis"
                    yAxisId="left"
                    dataKey="overall"
                    stroke="#59C36A"
                    fill="#59C36A"
                    activeDot={{ r: 3 }}
                    dot={{ r: 0 }}
                    isAnimationActive={false}
                />
            </LineChart>
        </ResponsiveContainer>
    );
};

export default OverviewTimeline;
